import { ContractError } from '../../../api';
import type { ItemKind } from '../constants/vocabulary';
import type {
  PutOverrideBody,
  RegistryEntry,
  RegistryEntryDetail,
  SetMaintenanceBody,
  SetTierBody,
  VersionedBody,
} from '../types/contract';

import { CTL_CONFIG_ENDPOINTS } from './endpoints';
import { checkedBody, parsed, reportFailure, reportingFailures, request } from './http';
import { putOverrideBodySchema, setMaintenanceBodySchema, setTierBodySchema, versionedBodySchema } from './requestSchemas';
import { parseRegistryList, registryEntryDetailSchema, registryEntrySchema } from './schemas';
import type { RegistryList } from './schemas';

export interface RegistryFilters {
  readonly includeArchived: boolean;
}

export interface ItemRef {
  readonly kind: ItemKind;
  readonly key: string;
}

const paramsOf = ({ includeArchived }: RegistryFilters): Record<string, unknown> => (includeArchived ? { includeArchived: 'true' } : {});

const savedEntry = (raw: unknown): RegistryEntry => parsed(registryEntrySchema, raw, 'saved machine');

export const fetchRegistry = async (plantId: string, filters: RegistryFilters, signal?: AbortSignal): Promise<RegistryList> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.REGISTRY(plantId), { signal, params: paramsOf(filters) });
  const list = reportingFailures(() => parseRegistryList(raw), { label: 'machine list' });
  if (list.unreadable > 0) reportFailure(new ContractError('Machines left out of the machine list', list.issues), { plantId, unreadable: list.unreadable });
  return list;
};

export const fetchRegistryEntry = async (plantId: string, entryId: string, signal?: AbortSignal): Promise<RegistryEntryDetail> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.REGISTRY_ENTRY(plantId, entryId), { signal });
  return parsed(registryEntryDetailSchema, raw, 'machine');
};

export const setMachineTier = async (plantId: string, entryId: string, body: SetTierBody): Promise<RegistryEntry> =>
  savedEntry(await request('put', CTL_CONFIG_ENDPOINTS.REGISTRY_TIER(plantId, entryId), { body: checkedBody(setTierBodySchema, body, 'machine criticality') }));

export const putMachineOverride = async (plantId: string, entryId: string, item: ItemRef, body: PutOverrideBody): Promise<RegistryEntry> =>
  savedEntry(
    await request('put', CTL_CONFIG_ENDPOINTS.REGISTRY_OVERRIDE(plantId, entryId, item.kind, item.key), { body: checkedBody(putOverrideBodySchema, body, 'machine override') }),
  );

export const deleteMachineOverride = async (plantId: string, entryId: string, item: ItemRef, body: VersionedBody): Promise<RegistryEntry> =>
  savedEntry(
    await request('delete', CTL_CONFIG_ENDPOINTS.REGISTRY_OVERRIDE(plantId, entryId, item.kind, item.key), { body: checkedBody(versionedBodySchema, body, 'override removal') }),
  );

export const setMachineMaintenance = async (plantId: string, entryId: string, body: SetMaintenanceBody): Promise<RegistryEntry> =>
  savedEntry(await request('put', CTL_CONFIG_ENDPOINTS.REGISTRY_MAINTENANCE(plantId, entryId), { body: checkedBody(setMaintenanceBodySchema, body, 'machine maintenance') }));

export const clearMachineMaintenance = async (plantId: string, entryId: string, body: VersionedBody): Promise<RegistryEntry> =>
  savedEntry(await request('delete', CTL_CONFIG_ENDPOINTS.REGISTRY_MAINTENANCE(plantId, entryId), { body: checkedBody(versionedBodySchema, body, 'maintenance clear') }));
